import React, { useEffect, useState } from 'react';
import { useParams, Navigate, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, XCircle, Loader } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';

export default function AuthVerify() {
  const { token } = useParams();
  const [status, setStatus] = useState('verifying'); // 'verifying', 'success', 'error'
  const [error, setError] = useState('');
  const [redirectPath, setRedirectPath] = useState('/dashboard');
  const { login, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (token) {
      verifyToken();
    }
  }, [token]);

  const verifyToken = async () => {
    try {
      const response = await api.verifyAuthToken(token);
      login(response.data.access_token);
      
      // Fetch user profile to determine redirect path
      try {
        const profileResponse = await api.getUserProfile();
        const userProfile = profileResponse.data;
        
        if (userProfile.role === 'admin') {
          setRedirectPath('/admin');
          toast.success('Welcome to admin panel!');
        } else {
          setRedirectPath('/dashboard');
          toast.success('Successfully signed in!');
        }
      } catch (profileError) {
        console.error('Failed to fetch profile:', profileError);
        setRedirectPath('/dashboard');
        toast.success('Successfully signed in!');
      }
      
      setStatus('success');
    } catch (error) {
      console.error('Token verification failed:', error);
      setStatus('error');
      setError(error.response?.data?.detail || 'Invalid or expired token');
      toast.error('Authentication failed. Please try again.');
    }
  };

  // Redirect if already authenticated and verification successful
  if (user && status === 'success') {
    return <Navigate to={redirectPath} replace />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 w-12 h-12 rounded-full flex items-center justify-center">
            {status === 'verifying' && (
              <div className="bg-blue-100">
                <Loader className="w-6 h-6 text-blue-600 animate-spin" />
              </div>
            )}
            {status === 'success' && (
              <div className="bg-green-100">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
            )}
            {status === 'error' && (
              <div className="bg-red-100">
                <XCircle className="w-6 h-6 text-red-600" />
              </div>
            )}
          </div>
          
          <CardTitle data-testid="verify-title">
            {status === 'verifying' && 'Verifying...'}
            {status === 'success' && 'Authentication Successful'}
            {status === 'error' && 'Authentication Failed'}
          </CardTitle>
          
          <CardDescription data-testid="verify-description">
            {status === 'verifying' && 'Please wait while we verify your authentication token.'}
            {status === 'success' && 'You will be redirected to your dashboard shortly.'}
            {status === 'error' && error}
          </CardDescription>
        </CardHeader>
        
        {status === 'error' && (
          <CardContent className="text-center">
            <Button 
              onClick={() => window.location.href = '/auth'}
              className="w-full"
              data-testid="back-to-auth-btn"
            >
              Back to Sign In
            </Button>
          </CardContent>
        )}
      </Card>
    </div>
  );
}